import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ArrowRight, Check, CheckCircle2, LoaderCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { supabase } from '../lib/supabase';
import './learn-more.css';

export const LearnMore = () => {
  const [state, setState] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');
  const submitting = useRef(false);
  const successHeading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const previous = document.title;
    document.title = 'Bring your ads to life | ADarveo';
    return () => { document.title = previous; };
  }, []);

  useEffect(() => {
    if (state === 'success') successHeading.current?.focus();
  }, [state]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting.current) return;
    const form = new FormData(event.currentTarget);
    const name = String(form.get('name') || '').trim();
    const email = String(form.get('email') || '').trim();
    if (!name) {
      setError('Please enter your name.');
      setState('error');
      return;
    }
    submitting.current = true;
    setState('sending');
    setError('');
    try {
      if (!supabase) throw new Error('Form unavailable');
      const { data, error: requestError } = await supabase.functions.invoke('submit-marketing-enquiry', {
        body: { name, email, website: String(form.get('website') || ''), consent: true },
        signal: AbortSignal.timeout(15000)
      });
      if (requestError?.context instanceof Response && [400, 429].includes(requestError.context.status)) {
        const details = await requestError.context.json().catch(() => null);
        setError(typeof details?.error === 'string' ? details.error : 'Please check your details and try again.');
        setState('error');
        return;
      }
      if (requestError || data?.ok !== true) throw new Error('Submission failed');
      setState('success');
    } catch {
      setState('error');
      setError('We couldn’t save your details. Please check your connection and try again.');
    } finally {
      submitting.current = false;
    }
  };

  return (
    <div className="enquiry-page">
      <header className="enquiry-header">
        <Link to="/" aria-label="ADarveo home"><Logo className="enquiry-logo" /></Link>
        <span>Scan. Experience. Measure.</span>
      </header>
      <main className="enquiry-main">
        <section className="enquiry-story" aria-labelledby="enquiry-title">
          <p className="enquiry-eyebrow">You’ve seen what’s possible.</p>
          <h1 id="enquiry-title">Now bring <br className="enquiry-desktop-break" />your ads <span>to life.</span></h1>
          <p className="enquiry-intro">Turn your posters, packaging and displays into experiences people can watch, interact with and act on.</p>
          <ul className="enquiry-benefits">
            <li><Check aria-hidden="true" /> No app download for your audience</li>
            <li><Check aria-hidden="true" /> Measure scans, video engagement and clicks</li>
          </ul>
          <div className="enquiry-art" aria-hidden="true">
            <img src="/learn-more/poster.png" alt="" width="941" height="1672" />
            <p>Physical advertising.<br /><strong>A whole new dimension.</strong></p>
          </div>
        </section>
        <section className="enquiry-card" aria-labelledby="enquiry-form-title">
          {state === 'success' ? (
            <div className="enquiry-success" role="status">
              <span className="enquiry-success-icon"><CheckCircle2 aria-hidden="true" /></span>
              <p className="enquiry-eyebrow">You’re on the list.</p>
              <h2 id="enquiry-form-title" ref={successHeading} tabIndex={-1}>Thanks for your interest.</h2>
              <p>We’ve received your details. The ADarveo team will be in touch to explore what’s possible for your advertising.</p>
              <Link className="enquiry-home-link" to="/">Explore ADarveo <ArrowRight aria-hidden="true" /></Link>
            </div>
          ) : (
            <>
              <p className="enquiry-eyebrow">Let’s make it happen</p>
              <h2 id="enquiry-form-title">Interested in ADarveo?</h2>
              <p className="enquiry-form-intro">Leave your details and let’s talk about bringing your advertising to life.</p>
              <form onSubmit={submit} aria-busy={state === 'sending'}>
                <label htmlFor="enquiry-name">Your name</label>
                <input id="enquiry-name" name="name" autoComplete="name" placeholder="Your name" required maxLength={120} disabled={state === 'sending'} />
                <label htmlFor="enquiry-email">Email address</label>
                <input id="enquiry-email" name="email" type="email" autoComplete="email" inputMode="email" autoCapitalize="none" spellCheck={false} placeholder="you@company.com" required maxLength={254} disabled={state === 'sending'} />
                <div className="enquiry-honeypot" aria-hidden="true">
                  <label htmlFor="enquiry-website">Leave this field empty</label>
                  <input id="enquiry-website" name="website" tabIndex={-1} autoComplete="off" />
                </div>
                {error && <p className="enquiry-error" role="alert">{error}</p>}
                <button type="submit" className="enquiry-submit" disabled={state === 'sending'}>
                  {state === 'sending' ? <>Saving your details <LoaderCircle className="enquiry-spinner" aria-hidden="true" /></> : <>I’m interested <ArrowRight aria-hidden="true" /></>}
                </button>
                <p className="enquiry-consent">By submitting, you agree that ADarveo may contact you about your enquiry. <Link to="/privacy">Privacy policy</Link>.</p>
              </form>
            </>
          )}
        </section>
      </main>
      <footer className="enquiry-footer"><span>© {new Date().getFullYear()} ADarveo</span><Link to="/privacy">Privacy policy</Link></footer>
    </div>
  );
};
